import { Link, Navigate, useParams } from 'react-router-dom';
import { Check, MessageCircle, ArrowUpRight, ArrowLeft, ArrowRight } from 'lucide-react';
import { Reveal, MaskedLine } from '../components/Reveal';
import { CTASection } from '../components/CTASection';
import { getService, services } from '../data/services';
import { site } from '../data/site';
import { usePageMeta } from '../components/ScrollToTop';

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);

  usePageMeta(
    service ? `${service.title} — TheBigHub` : 'Service — TheBigHub',
    service ? service.short : ''
  );

  if (!service) return <Navigate to="/services" replace />;

  const idx = services.findIndex((s) => s.slug === slug);
  const next = services[(idx + 1) % services.length];
  const Icon = service.icon;
  const waText = `${site.whatsappPrefill.split('?text=')[0]}?text=${encodeURIComponent(
    `Hello TheBigHub, I would like to discuss your ${service.title} service.`
  )}`;

  return (
    <main data-testid={`service-page-${slug}`}>
      <section className="grain relative overflow-hidden bg-pine pt-32 text-white sm:pt-40">
        <div
          className="pointer-events-none absolute -right-40 -top-32 h-[480px] w-[480px] rounded-full blur-[130px]"
          style={{ backgroundColor: `${service.accent}33` }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-5 pb-20 sm:px-8 sm:pb-28">
          <Reveal>
            <Link
              to="/services"
              data-testid="service-back-link"
              className="mb-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:text-goldlight"
            >
              <ArrowLeft size={14} /> All Services
            </Link>
          </Reveal>
          <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <Reveal delay={0.05}>
                <div className="mb-6 flex items-center gap-4">
                  <span
                    className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-forest"
                  >
                    <Icon size={22} strokeWidth={1.8} />
                  </span>
                  <span className="font-mono text-xs uppercase tracking-[0.28em] text-goldlight">
                    Service {service.num} / 08
                  </span>
                </div>
              </Reveal>
              <h1 className="text-balance font-display text-4xl font-bold leading-[1.08] sm:text-5xl lg:text-6xl">
                <MaskedLine delay={0.15}>{service.headline}</MaskedLine>
              </h1>
              <Reveal delay={0.35}>
                <p className="mt-7 max-w-xl text-base leading-relaxed text-white sm:text-lg">
                  {service.description}
                </p>
              </Reveal>
              <Reveal delay={0.45}>
                <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                  <a href={waText} target="_blank" rel="noopener noreferrer" data-testid="service-whatsapp-cta" className="btn-mint">
                    <MessageCircle size={17} /> {service.cta}
                  </a>
                  <Link to="/contact" data-testid="service-contact-cta" className="btn-outline-light">
                    Request a Consultation <ArrowUpRight size={16} />
                  </Link>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.3} className="relative">
              <div
                className="absolute -left-4 -top-4 h-full w-full rounded-2xl border"
                style={{ borderColor: "#A8D5BA55" }}
                aria-hidden="true"
              />
              <div className="img-frame aspect-[4/3] shadow-[0_44px_100px_-40px_rgba(0,0,0,0.8)]">
                <img src={service.image} alt={service.imageAlt} loading="eager" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-paper">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <Reveal>
            <div className="mb-12 flex items-center gap-4">
              <span className="gold-rule" aria-hidden="true" />
              <span className="eyebrow">What's Included</span>
            </div>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="service-offerings">
            {service.offerings.map((o, i) => (
              <Reveal key={o} delay={(i % 3) * 0.07}>
                <div className="card-lift flex h-full items-center gap-4 rounded-xl border-2 border-forest bg-forest p-5">
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15 text-goldlight"
                  >
                    <Check size={15} strokeWidth={3} />
                  </span>
                  <span className="text-sm font-semibold text-white">{o}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <Link
              to={next.linkOverride || `/services/${next.slug}`}
              data-testid="service-next-link"
              className="group mt-20 flex items-center justify-between rounded-2xl border-2 border-forest bg-forest p-7 transition-colors duration-300 hover:bg-moss sm:p-9"
            >
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-goldlight/80">
                  Next Service — {next.num}
                </p>
                <p className="mt-2 font-display text-xl font-bold text-white sm:text-2xl">
                  {next.title}
                </p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-forest transition-transform duration-300 group-hover:translate-x-1.5">
                <ArrowRight size={18} />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
